import { Fragment, useState, type ReactNode } from 'react';
import {
  RecipientSource,
  textSendRecipientsInWords,
  type CommitteeFactsDto,
  type WorkOrderDto,
} from '@signalvote/shared';
import {
  PhonePreviewAside,
  PhonePreviewButton,
  WorkOrderPaymentPanel,
  checkoutErrorMessage,
  checkoutMinimumNote,
  describeSendTime,
  formatCents,
  lineQuantity,
  timeZoneName,
  useWorkOrderPayment,
} from '@/components/work-orders';
import { useCommitteeFacts, useWorkOrder, useWorkOrderCheckoutPreview, useWorkOrderCheckoutResult } from '@/hooks/queries';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { ExpressAttestation, isSigned, signedAsWords } from './ExpressAttestation';
import { ExpressStep } from './ExpressStep';
import { sendsInsideRegistrationWait } from './expressProgress';
import type { ExpressStepViewProps } from './expressStepViews';
import { CHECKOUT_LINE_COPY } from './ExpressTextSteps';
import { useExpressTextSend } from './useExpressTextSend';

/**
 * Review, sign and pay: who gets the text and when, every line of the invoice as the server prices
 * it, the signature when About your committee ran, and the card payment.
 */
export function ReviewStep({ step, steps, campaign, onBack }: ExpressStepViewProps) {
  const send = useExpressTextSend();
  const workOrder = send.workOrder;
  const paired = useWorkOrder(workOrder?.details.pairedVoterDataWorkOrderId ?? undefined);
  const committee = useCommitteeFacts().data;

  if (!workOrder || !committee || paired.isLoading) {
    const failed = send.isError || paired.isError;
    return (
      <ExpressStep step={step} onBack={onBack} error={failed ? 'We couldn’t load your order. Refresh to try again.' : null}>
        {!failed && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading your order…
          </p>
        )}
      </ExpressStep>
    );
  }

  return (
    <ReviewOrder
      step={step}
      onBack={onBack}
      campaign={campaign}
      workOrder={workOrder}
      pairedOrder={paired.data ?? null}
      committee={committee}
      registrationRan={steps?.includes('registration') ?? false}
    />
  );
}

interface ReviewOrderProps extends Pick<ExpressStepViewProps, 'step' | 'onBack' | 'campaign'> {
  workOrder: WorkOrderDto;
  pairedOrder: WorkOrderDto | null;
  committee: CommitteeFactsDto;
  registrationRan: boolean;
}

function ReviewOrder({ step, onBack, campaign, workOrder, pairedOrder, committee, registrationRan }: ReviewOrderProps) {
  // An Invoiced send's lines come back with its raised invoice instead.
  const preview = useWorkOrderCheckoutPreview(workOrder.id);
  const minimumNote = checkoutMinimumNote(preview.data);
  const payment = useWorkOrderPayment({
    workOrder,
    totalCents: preview.data?.totalCents ?? null,
    ready: (!registrationRan || isSigned(committee)) && !minimumNote,
    confirmedVoterDataPriceCents:
      workOrder.details.recipientSource === RecipientSource.BoughtList ? (pairedOrder?.voterData?.priceCents ?? null) : null,
  });
  const raised = useWorkOrderCheckoutResult(workOrder.id).data;
  // Once Pay raises the invoice, its lines are what the card pays.
  const bill = raised ?? preview.data;
  // Back on the card step closes the card form and shows the order again; the invoice stays raised, so
  // Pay reopens the form on it. Nothing about the order can change once its invoice is raised.
  const [cardOpen, setCardOpen] = useState(true);
  const cardStep = payment.stage === 'card';
  const back = payment.stage === 'idle' ? onBack : cardStep && cardOpen ? () => setCardOpen(false) : undefined;
  const primary = cardStep && !cardOpen ? { ...payment.action, onPress: () => setCardOpen(true) } : payment.action;
  // Read from the send, not from the steps shown: once its invoice is raised the steps collapse to this one.
  const signedAs = workOrder.isFirstSend ? signedAsWords(committee) : null;

  const content = {
    body: workOrder.details.messageText ?? '',
    paidForDisclaimer: workOrder.details.paidForDisclaimer ?? '',
    imageUrl: workOrder.details.imagePreviewUrl,
  };
  const zone = timeZoneName(campaign?.timeZoneId);
  const when = describeSendTime(workOrder.sendDate, workOrder.sendTime);
  const waitsOnRegistration = workOrder.isFirstSend && sendsInsideRegistrationWait(workOrder.sendDate, getCampaignTodayString());
  const summary: [string, ReactNode][] = [
    ['To', textSendRecipientsInWords(workOrder.details.recipientSource, workOrder.recipientCount ?? 0, workOrder.status)],
    [
      'Sends',
      <>
        {when ? `${when}${zone ? `, ${zone}` : ''}` : 'No time yet'}
        {waitsOnRegistration && (
          <span className="block text-sm text-muted-foreground">
            If registration hasn’t cleared by then, it goes out as soon as it has.
          </span>
        )}
      </>,
    ],
    ['From', committee.facts.legalName ?? campaign?.name ?? ''],
  ];

  return (
    <ExpressStep
      step={step}
      onBack={back}
      aside={<PhonePreviewAside {...content} />}
      summary={
        bill && <span className="text-lg font-semibold tabular-nums text-foreground">{formatCents(bill.totalCents)}</span>
      }
      primary={primary}
    >
      <div className="space-y-8">
        <dl className="grid grid-cols-[72px_1fr] gap-x-4 gap-y-2 text-[1.0625rem]">
          {summary.map(([term, value]) => (
            <Fragment key={term}>
              <dt className="text-sm leading-7 text-muted-foreground">{term}</dt>
              <dd className="text-foreground">{value}</dd>
            </Fragment>
          ))}
        </dl>
        <PhonePreviewButton {...content} />

        {bill ? (
          <table aria-label="What you pay" className="w-full text-sm">
            <tbody>
              {bill.lines.map((line) => {
                const quantity = lineQuantity(line);
                const copy = CHECKOUT_LINE_COPY[line.kind];
                return (
                  <tr key={line.kind} className="border-b border-border">
                    <td className="py-3 pr-3">
                      <span className="font-medium text-foreground">{copy.label(line.quantity)}</span>
                      {quantity && <span className="text-muted-foreground"> · {quantity}</span>}
                      {copy.why && <span className="block text-muted-foreground">{copy.why}</span>}
                    </td>
                    <td className="py-3 text-right align-top tabular-nums">{formatCents(line.amountCents)}</td>
                  </tr>
                );
              })}
              <tr className="font-semibold">
                <td className="pt-3">Total</td>
                <td className="pt-3 text-right tabular-nums">{formatCents(bill.totalCents)}</td>
              </tr>
            </tbody>
          </table>
        ) : preview.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {checkoutErrorMessage(preview.error, 'We couldn’t price your order. Refresh to try again.')}
          </p>
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Pricing your order…
          </p>
        )}

        {minimumNote && (
          <p role="status" className="text-sm text-muted-foreground">
            {minimumNote}
          </p>
        )}

        {registrationRan && payment.stage === 'idle' ? (
          <ExpressAttestation />
        ) : (
          signedAs && (
            <p role="status" className="text-sm text-muted-foreground">
              {signedAs}
            </p>
          )
        )}

        {(cardOpen || !cardStep) && <WorkOrderPaymentPanel payment={payment} />}

        <p className="text-sm leading-relaxed text-muted-foreground">
          After you pay, we email you a private status link to follow your text, no password needed. You can also
          sign in anytime and open Orders, where your text is at the top.
        </p>
      </div>
    </ExpressStep>
  );
}
